import React, { useState } from "react";
import { Box, Grid, Stack, useTheme, FormControl, InputLabel, Select, MenuItem, Card, Typography, Chip, Avatar } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import BoltIcon from "@mui/icons-material/Bolt";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import WavesIcon from "@mui/icons-material/Waves";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import RecommendIcon from "@mui/icons-material/Recommend";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { StatCard, RadarChart, LineChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { sampleUsers, userProgressStats, userWellnessDimensions, userAiInsights, recoveryTrends } from "../data";

export const UserProgress: React.FC = () => {
  const theme = useTheme();
  const [selectedUser, setSelectedUser] = useState(sampleUsers[0].id);
  const user = sampleUsers.find((u) => u.id === selectedUser) || sampleUsers[0];

  const statCards = [
    { metric: userProgressStats.recovery_score, icon: FavoriteIcon },
    { metric: userProgressStats.sleep_performance, icon: BedtimeIcon },
    { metric: userProgressStats.day_strain, icon: BoltIcon },
    { metric: userProgressStats.hrv, icon: MonitorHeartIcon },
    { metric: userProgressStats.resting_heart_rate, icon: VolunteerActivismIcon },
    { metric: userProgressStats.stress_score, icon: SelfImprovementIcon },
    { metric: userProgressStats.sleep_efficiency, icon: WavesIcon },
    { metric: userProgressStats.calories_burn, icon: LocalFireDepartmentIcon },
  ];

  const insightIcons = { highlight: TipsAndUpdatesIcon, recommendation: RecommendIcon, watch: VisibilityIcon };
  const insightColors = { highlight: theme.palette.success.main, recommendation: theme.palette.info.main, watch: theme.palette.warning.main };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>Select Participant</InputLabel>
            <Select value={selectedUser} label="Select Participant" onChange={(e) => setSelectedUser(e.target.value)}>
              {sampleUsers.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name} — {u.department}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip label={user.role} size="small" variant="outlined" />
          <Chip label={user.department} size="small" color="primary" variant="outlined" />
        </Box>

        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <DashboardCard title="Wellness Dimensions" subtitle="Individual performance vs benchmark.">
              <RadarChart
                data={userWellnessDimensions}
                seriesKeys={["value", "benchmark"]}
                seriesLabels={["Individual", "Benchmark"]}
                metricKey="metric"
                height={350}
                colors={[theme.palette.primary.main, theme.palette.text.disabled]}
              />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <DashboardCard title="Recovery Trend" subtitle="Individual recovery score over time.">
              <LineChart
                data={recoveryTrends}
                xAxisKey="label"
                series={[{ dataKey: "recovery_score", label: "Recovery Score", color: theme.palette.success.main, area: true }]}
                height={350}
                yAxisMin={0}
                yAxisMax={100}
                showMinMaxLines
                referenceMin={22}
                referenceMax={95}
              />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="AI Insights" subtitle="AI-generated observations and recommendations for this participant.">
              <Stack spacing={2}>
                {userAiInsights.map((insight, index) => {
                  const IconComp = insightIcons[insight.type];
                  const color = insightColors[insight.type];
                  return (
                    <Card key={index} variant="outlined" sx={{ p: 2, borderLeft: `4px solid ${color}` }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Avatar sx={{ bgcolor: `${color}20`, width: 40, height: 40 }}>
                          <IconComp sx={{ color, fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{insight.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{insight.description}</Typography>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Stack>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
